import React, { useMemo, useRef, useEffect } from "react";
import { CustomReactTable } from "../../../Component/Common/CustomReactTable";
import { FEATURES, hasAccess } from "../../../helper";
import EditIcon from "../../../../image/Group15.png";
import DeleteIcon from "../../../../image/deleteIcon1.png";
import { BsEye } from "react-icons/bs";
import { colourLabel, getColourClassName, maskMobileNumber } from "./voterHelpers";

const SelectAllCheckbox = ({ checked, indeterminate, onChange }) => {
  const checkboxRef = useRef(null);
  useEffect(() => {
    if (checkboxRef.current) checkboxRef.current.indeterminate = indeterminate;
  }, [indeterminate]);
  return (
    <input
      ref={checkboxRef}
      type="checkbox"
      className="voter-select-checkbox"
      checked={checked}
      onChange={onChange}
      aria-label="Select all voters on this page"
    />
  );
};

// Shared voter results table, reused by Voter Search / Age Wise Search /
// Alphabetical Search so table markup + custom cell renderers (photo, colour
// swatch, mobile mask, EPIC link, address tooltip, voted pill, action icons)
// aren't duplicated three times.
//
// `voters` can be the whole voter list (hundreds of thousands of rows), so:
// - rows are virtualized (CustomReactTable `virtualize`) - only what's on
//   screen is in the DOM;
// - cells are rendered lazily from the raw voter via `Cell`, instead of
//   pre-building JSX for every voter up front;
// - volatile inputs (selection, mobile masking, handlers) are read from
//   `liveRef`, so columns stay stable and react-table doesn't reprocess the
//   whole list on every checkbox click. The visible cells still re-render
//   with the latest values because the table body re-renders with us.
export const VoterTable = ({
  voters,
  tableLoading,
  loadProgress,
  userLogin,
  selectable = true,
  selectedIdSet,
  selectedOnPageCount = 0,
  allSelected = false,
  onToggleSelect,
  onToggleSelectAll,
  showCastNo = false,
  showMobileNumbers = false,
  onView,
  onEdit,
  onDelete,
}) => {
  const liveRef = useRef({});
  liveRef.current = {
    selectedIdSet,
    selectedOnPageCount,
    allSelected,
    votersLength: voters.length,
    onToggleSelect,
    onToggleSelectAll,
    showMobileNumbers,
    userType: userLogin?.user_type,
    onView,
    onEdit,
    onDelete,
  };

  // A new result set starts back at the top of the table.
  const scrollRef = useRef(null);
  const firstVoterId = voters[0]?.id;
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [firstVoterId]);

  const voterColumns = useMemo(
    () => [
      ...(selectable
        ? [
            {
              id: "selection",
              Header: () => {
                const live = liveRef.current;
                return (
                  <SelectAllCheckbox
                    checked={live.allSelected}
                    indeterminate={live.selectedOnPageCount > 0 && live.selectedOnPageCount < live.votersLength}
                    onChange={live.onToggleSelectAll}
                  />
                );
              },
              disableSortBy: true,
              Cell: ({ row: { original: item } }) => (
                <div className="voter-select-cell">
                  <input
                    type="checkbox"
                    className="voter-select-checkbox"
                    checked={!!liveRef.current.selectedIdSet?.has(item.id)}
                    onChange={() => liveRef.current.onToggleSelect?.(item)}
                    aria-label={`Select voter ${item.epic_no || item.sr_no}`}
                  />
                </div>
              ),
            },
          ]
        : []),
      {
        Header: "Photo",
        id: "photo",
        disableSortBy: true,
        Cell: ({ row: { original: item } }) =>
          item?.profile_image ? (
            <img src={item.profile_image} alt={item?.first_name_e || "Voter"} className="voter-avatar-thumb" />
          ) : (
            <span className="voter-avatar-thumb voter-avatar-fallback">
              {(item?.first_name_e || "?").charAt(0).toUpperCase()}
            </span>
          ),
      },
      {
        Header: "Color",
        id: "color_swatch",
        disableSortBy: true,
        Cell: ({ row: { original: item } }) => (
          <span
            className={`voter-color-indicator ${getColourClassName(item?.colour)}`}
            title={colourLabel(item?.colour)}
          />
        ),
      },
      { Header: "Booth No.", accessor: "booth_no" },
      { Header: "VSRN", accessor: "voter_sr_number" },
      {
        Header: "H.No",
        id: "house_no",
        accessor: (item) => String(item.house_no ?? ""),
        Cell: ({ value }) => <span>{value || "-"}</span>,
      },
      { Header: "First Name", accessor: "first_name_e" },
      { Header: "Relative Name", accessor: "last_name_e" },
      { Header: "Gender", accessor: "gender_e" },
      { Header: "Age", accessor: "age" },
      {
        Header: "Mobile Number",
        accessor: "mobile_no",
        Cell: ({ value }) =>
          liveRef.current.showMobileNumbers ? value || "-" : maskMobileNumber(value),
      },
      ...(showCastNo
        ? [
            {
              Header: "Category",
              id: "cast_no",
              accessor: (item) => item?.category ?? item?.cast_no ?? item?.sr_no ?? "-",
            },
          ]
        : []),
      {
        Header: "EPIC No.",
        id: "epic_num",
        disableSortBy: true,
        Cell: ({ row: { original: item } }) => (
          <button
            type="button"
            className="voter-epic-link"
            onClick={() => {
              if (hasAccess(liveRef.current.userType, FEATURES.EDIT_VOTER)) liveRef.current.onEdit?.(item);
            }}
          >
            {item?.epic_no || "-"}
          </button>
        ),
      },
      {
        Header: "Address",
        accessor: "address_e",
        Cell: ({ value }) => (
          <span title={value || ""}>
            {value ? (value.length > 12 ? `${value.slice(0, 12)}...` : value) : "-"}
          </span>
        ),
      },
      {
        Header: "Voted",
        id: "voteds",
        accessor: (item) => (item.is_voted === true ? "Yes" : "No"),
        Cell: ({ value }) => (
          <span className={`voter-voted-pill ${value === "Yes" ? "yes" : "no"}`}>{value}</span>
        ),
      },
      {
        Header: "Action",
        id: "action",
        disableSortBy: true,
        Cell: ({ row: { original: item } }) => {
          const live = liveRef.current;
          return (
            <div className="actionBtn">
              <BsEye aria-label="View voter" role="button" tabIndex={0} onClick={() => live.onView?.(item)} />
              {hasAccess(live.userType, FEATURES.EDIT_VOTER) && (
                <img src={EditIcon} alt="Edit voter" onClick={() => live.onEdit?.(item)} />
              )}
              {hasAccess(live.userType, FEATURES.DELETE_VOTER) && (
                <img src={DeleteIcon} alt="Delete voter" onClick={() => live.onDelete?.(item)} />
              )}
            </div>
          );
        },
      },
    ],
    [selectable, showCastNo]
  );

  return (
    <div className="voter-table-section">
      {tableLoading && (
        <div className="voter-table-overlay">
          <div className="voter-table-loading">
            <div className="voter-table-spinner" />
            {loadProgress > 0 && loadProgress < 100 && (
              <span className="voter-table-loading-text">Loading voters… {loadProgress}%</span>
            )}
          </div>
        </div>
      )}
      <div className="voter-table-responsive">
        <CustomReactTable
          columns={voterColumns}
          data={voters}
          tableLoading={tableLoading}
          type="fullVoter"
          containerClassName="voter-table-container"
          tableClassName="voter-list-table"
          containerRef={scrollRef}
          virtualize
        />
      </div>
    </div>
  );
};
